import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Pagination,
  TextField,
  Typography
} from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';

import { paths } from 'src/routes/paths';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { EntityLinkType } from 'src/services/common.types';
import { useAddContactLinkMutation, useGetContactLinksQuery, useGetOrganizationContactsQuery, useRemoveContactLinkMutation } from 'src/services/contacts/contacts.service';
import { ContactLinkEntityType, Contact } from 'src/services/contacts/contacts.types';
import {
  useGetDealContactLinksQuery
} from 'src/services/deal-flow/deal-flow.service';
import { IDealContactLink } from 'src/services/deal-flow/deal-flow.types';

import { ConfirmDialog } from 'src/components/custom-dialog';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { LoadingCardItem } from 'src/sections/admin/billing/payment/payment-card-item';
import AddContactModal from 'src/sections/contacts/modal/add-contact-modal';
import { useGetCompanyContactLinksQuery } from 'src/services/connections/connections.service';
import { useGetMeetingContactLinksQuery } from 'src/services/meetings/meetings.service';

// ----------------------------------------------------------------------

const DEFAULT_PAGE: PageOptions = {
  page: 1,
  per_page: 5,
};

type Props = {
  entityId: string;
  entityType: ContactLinkEntityType;
  enableTypeSelection?: boolean;
};

export function LinkedContactsCard({ entityId, entityType, enableTypeSelection = false }: Props) {

  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const authContext = useAuthContext();

  const [pageOptions, setPageOptions] = useState(DEFAULT_PAGE);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  const addLinkControl = useBoolean();
  const addContactModalControl = useBoolean();

  const { data: contactContactLinks, isLoading: isLoadingC2CLinks } = useGetContactLinksQuery({
    contact_id: entityId,
    params: pageOptions
  }, {
    skip: !entityId || entityType !== ContactLinkEntityType.CONTACT,
  });

  const { data: dealContactLinksData, isLoading: isLoadingDealLinks } = useGetDealContactLinksQuery({
    dealId: entityId,
    page: pageOptions.page,
    perPage: pageOptions.per_page,
    searchQuery,
  }, {
    skip: !entityId || entityType !== ContactLinkEntityType.DEAL,
  });

  const { data: meetingContactLinksData, isLoading: isLoadingMeetingLinks } = useGetMeetingContactLinksQuery({
    meetingId: entityId,
    params: {
      page: pageOptions.page,
      per_page: pageOptions.per_page,
      search_query: searchQuery,
    }
  }, {
    skip: !entityId || entityType !== ContactLinkEntityType.MEETING,
  });

  const { data: companyContactLinksData, isLoading: isLoadingCompanyLinks } = useGetCompanyContactLinksQuery({
    profile_id: entityId,
    params: {
      page: pageOptions.page,
      per_page: pageOptions.per_page,
      search_query: searchQuery,
    }
  }, {
    skip: !entityId || entityType !== ContactLinkEntityType.COMPANY_PROFILE,
  });

  const { currentData: contacts, isLoading: isContactsLoading } = useGetOrganizationContactsQuery(
    {
      org_id: authContext.organization.id,
      page: DEFAULT_PAGE,
      search_query: contactSearchQuery
    }
  );

  const [addContactLinks, { isLoading: isAddingContactLinks }] = useAddContactLinkMutation();
  const [removeContactLink, { isLoading: isRemovingContactLink }] = useRemoveContactLinkMutation();

  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedLinkType, setSelectedLinkType] = useState<EntityLinkType>(EntityLinkType.OTHER);

  const contactLinksData = useMemo(() => {
    switch (entityType) {
      case ContactLinkEntityType.DEAL:
        return dealContactLinksData;
      case ContactLinkEntityType.CONTACT:
        return contactContactLinks;
      case ContactLinkEntityType.COMPANY_PROFILE:
        return companyContactLinksData;
      case ContactLinkEntityType.MEETING:
        return meetingContactLinksData;
      default:
        return null;
    }

  }, [entityType, dealContactLinksData, meetingContactLinksData, companyContactLinksData, contactContactLinks]);

  const isLoadingContactLinks = useMemo(() => {
    switch (entityType) {
      case ContactLinkEntityType.DEAL:
        return isLoadingDealLinks;
      case ContactLinkEntityType.CONTACT:
        return isLoadingC2CLinks
      case ContactLinkEntityType.COMPANY_PROFILE:
        return isLoadingCompanyLinks;
      case ContactLinkEntityType.MEETING:
        return isLoadingMeetingLinks;
      default:
        return false;
    }

  }, [entityType, isLoadingDealLinks, isLoadingCompanyLinks, isLoadingMeetingLinks, isLoadingC2CLinks]);

  const handleSearchChange = useMemo(
    () => debounce((value: string) => {
      setSearchQuery(value);
    }, 500),
    [setSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    addLinkControl.onFalse();
    setSelectedContact(null);
    setSelectedLinkType(EntityLinkType.OTHER);
    setContactSearchQuery('');
  }, [addLinkControl]);

  const handleAddContacts = async () => {
    if (!selectedContact) return;

    try {
      await addContactLinks({
        data: {
          entity_id: entityId,
          entity_type: entityType,
          contacts: [{
            id: selectedContact.id,
            type: selectedLinkType,
          }],
        },
      }).unwrap();

      enqueueSnackbar(t('contacts.api.add_contact_links.success'), { variant: 'success' });
      onLinkModalClose();
    } catch (error) {
      enqueueSnackbar(t('contacts.api.add_contact_links.default_error'), { variant: 'error' });
    }
  };

  const handleRemoveContact = useCallback(async (linkId: string) => {
    try {
      await removeContactLink({
        entity_id: entityId,
        entity_type: entityType,
        link_id: linkId,
      }).unwrap();

      enqueueSnackbar(t('contacts.api.remove_contact_link.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('contacts.api.remove_contact_link.default_error'), { variant: 'error' });
    }
  }, [entityId, entityType, removeContactLink, enqueueSnackbar, t]);

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onChangePage = useCallback((event: React.ChangeEvent<unknown>, pageNumber: number) => {
    setPageOptions((prev) => ({
      ...prev,
      page: pageNumber
    }));
  }, []);

  const onAddLink = () => {
    addLinkControl.onTrue();
  };

  const linkableContacts = useMemo(() => {
    if (isContactsLoading || isLoadingContactLinks) return [];

    return contacts?.results?.filter((contact) => {
      const isAlreadyLinked = contactLinksData?.results?.some((link) => link.contact.id === contact.id);
      if (entityType === ContactLinkEntityType.CONTACT) {
        return contact.id !== entityId && !isAlreadyLinked;
      }

      return !isAlreadyLinked;
    })
  }, [contacts, contactLinksData, isContactsLoading, isLoadingContactLinks, entityId, entityType]);

  const onContactCreateSuccess = useCallback((newContact: Contact) => {

    addContactModalControl.onFalse();
    setSelectedContact(newContact);
    setSelectedLinkType(EntityLinkType.OTHER);
    setContactSearchQuery('');

    // refetch contacts to include newly created contact
    setPageOptions((prev) => ({ ...prev, page: 1 }));
  }, [addContactModalControl]);

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
              <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                <Iconify
                  icon="material-symbols:person-outline" />
                <Box>{t('contacts.linked_contacts.title')}</Box>
              </Stack>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddLink}
                startIcon={<Iconify icon="eva:plus-fill" />}
                sx={{
                  color: 'text.primary',
                  textTransform: 'capitalize',
                }}>
                {t('contacts.linked_contacts.add_link')}
              </Button>
            </Stack>
          }
        />
        <CardContent sx={{ mb: 2 }}>
          {isLoadingContactLinks && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <LoadingCardItem />
              <LoadingCardItem />
              <LoadingCardItem />
            </Stack>
          )}

          {(!isLoadingContactLinks && !(contactLinksData as any)?.results?.length) && (
            <Stack sx={{ width: '100%' }} spacing={2} justifyContent="center" alignItems="center">
              <EmptyContent
                title={t('contacts.linked_contacts.empty')}
                description={t('contacts.linked_contacts.empty_description')}
                sx={{ width: '100%' }}
              />
            </Stack>
          )}

          {(!isLoadingContactLinks && !!(contactLinksData as any)?.results?.length) && (
            <Stack direction="column" gap={2} justifyContent="start" alignItems="start" sx={{ width: '100%' }}>
              <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {(contactLinksData as any)?.results?.map((link: IDealContactLink) => (
                  <Fragment key={link.id}>
                    <ListItem
                      sx={{ pt: 1 }}
                      secondaryAction={
                        <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() => handleRemoveContact(link.id)}
                            disabled={isRemovingContactLink}
                          >
                            {t('contacts.linked_contacts.remove')}
                          </Button>
                        </Stack>
                      }>
                      <ListItemText
                        sx={{ pr: 4 }}
                        primary={
                          <Stack direction="row" gap={1} justifyContent="start" alignItems="center">
                            <Link
                              to={paths.dashboard.contacts.detail(link.contact.id)}
                              style={{
                                textDecoration: 'none',
                                color: theme.palette.text.primary,
                                cursor: 'pointer',

                              }}
                            >
                              <TruncatedTextField
                                text={`${link.contact.first_name} ${link.contact.last_name}`}
                                mode="characters"
                                limit={30}
                                showMoreEnabled={false}
                                variant="subtitle2"
                                sx={{
                                  textTransform: 'capitalize',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              />
                            </Link>
                          </Stack>
                        }
                        secondary={enableTypeSelection ? t(`enums.entity_link_type.${link.type}`) : link.contact.contact_type}
                        secondaryTypographyProps={{ variant: 'caption', textTransform: 'capitalize' }}
                      />
                    </ListItem>
                    <Divider variant="middle" component="li" />
                  </Fragment>
                ))}
              </List>

              <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: '100%', mt: 2 }}>
                <Pagination
                  count={(contactLinksData as any)?.count ? Math.ceil((contactLinksData as any).count / pageOptions.per_page) : 1}
                  page={pageOptions.page}
                  onChange={onChangePage}
                  size="small"
                />
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={addLinkControl.value}
        title={
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="h6">
              {t('contacts.linked_contacts.add_dialog_title')}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={addContactModalControl.onTrue}
            >
              {t('common.create')}
            </Button>

          </Stack>
        }
        onClose={onLinkModalClose}
        content={
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Autocomplete
              multiple={false}
              filterSelectedOptions
              options={linkableContacts || []}
              loading={isSearchingContacts}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('contacts.linked_contacts.contact_autocomplete_label')}
                  placeholder={t('contacts.linked_contacts.contact_autocomplete_placeholder')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              onInputChange={(_e, value) => onSearchQueryChange(value)}
              onChange={(_event, value) => setSelectedContact(value as Contact)}
              value={selectedContact}
            />

            {
              enableTypeSelection && (
                <Autocomplete
                  disablePortal
                  options={Object.values(EntityLinkType)}
                  getOptionLabel={(option) => t(`enums.entity_link_type.${option}`)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('contacts.linked_contacts.link_type_label')}
                    />
                  )}
                  onChange={(_event, value) => setSelectedLinkType(value as EntityLinkType)}
                  value={selectedLinkType}
                />
              )
            }
          </Stack>
        }
        action={
          <Button
            variant="contained"
            disabled={!selectedContact || isAddingContactLinks}
            onClick={handleAddContacts}
          >
            {t('contacts.linked_contacts.add')}
          </Button>
        }
      />

      <AddContactModal
        open={addContactModalControl.value}
        onClose={addContactModalControl.onFalse}
        companyId={entityType === ContactLinkEntityType.COMPANY_PROFILE ? entityId : undefined}
        onSuccess={onContactCreateSuccess}
      />

    </>
  );
}